import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useInterval } from 'react-use';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { BackendMarketSearchLayout } from '#src/components/market/BackendMarketSearchLayout/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useBackendMarketCardsSearch } from '#src/hooks/search/useBackendMarketCardsSearch.ts';
import { MARKET_CARDS_SEARCH_QUERY } from '#src/hooks/search/useMarketCardsSearch.ts';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { BackendCardResultsFromGraphQL } from '#src/searchCards/BackendCardResultsFromGraphQL/index.tsx';
import { backendPrimaryMarketFilters as footballBackendPrimaryMarketFilters } from '#src/searchCards/football/cardFilters.tsx';
import { backendPrimaryMarketFilters as mlbBackendPrimaryMarketFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { backendPrimaryMarketFilters as nbaBackendPrimaryMarketFilters } from '#src/searchCards/nba/cardFilters.tsx';

const auctionSorts: GallerySortNames = [
  'Cards On Sale Newly Listed',
  'Cards On Sale Ending Soon',
  'Cards On Sale Highest Price',
  'Cards On Sale Lowest Price',
  'Cards On Sale Highest Average Score',
];

const DEFAULT_AUCTION_SORT: GallerySortName = 'Cards On Sale Ending Soon';

const ENDING_SOON_REFRESH_INTERVAL = 60_000;

const backendPrimaryMarketFiltersBySport = {
  [Sport.FOOTBALL]: footballBackendPrimaryMarketFilters,
  [Sport.NBA]: nbaBackendPrimaryMarketFilters,
  [Sport.BASEBALL]: mlbBackendPrimaryMarketFilters,
};

type Props = {
  sport: Sport;
  banner?: ReactNode;
};

const BackendAuctionsSearchContent = ({ sport, banner }: Props) => {
  const { formatMessage } = useIntl();

  const cardFilters =
    backendPrimaryMarketFiltersBySport[
      sport as keyof typeof backendPrimaryMarketFiltersBySport
    ]?.() ?? [];

  const defaultFilters = useDefaultFilters({ auction: true });

  const {
    query,
    page,
    refinements,
    facets,
    sortInputs,
    advancedFilters,
    filtersLoading,
    currentSortName,
    makeGqlSearchContextValue,
  } = useBackendMarketCardsSearch({
    sport,
    cardFilters,
    defaultFilters,
    defaultSort: DEFAULT_AUCTION_SORT,
  });

  const { data, loading, previousData, refetch } = useQuery(
    MARKET_CARDS_SEARCH_QUERY,
    {
      skip: filtersLoading,
      variables: {
        query,
        page,
        pageSize: 20,
        advancedFilters,
        sorts: sortInputs,
        refinements,
        facets,
        onSaleOnly: true,
      },
    }
  );

  const sortedByEndingSoon = currentSortName === DEFAULT_AUCTION_SORT;

  useInterval(
    () => {
      if (!document.hidden) {
        refetch();
      }
    },
    sortedByEndingSoon ? ENDING_SOON_REFRESH_INTERVAL : null
  );

  const safeData = data ?? previousData;
  const searchCards = safeData?.searchCards;
  const hitSlugs = searchCards?.hits?.map(h => h.slug) ?? [];

  return (
    <GQLSearchContext value={makeGqlSearchContextValue(searchCards)}>
      <BackendMarketSearchLayout
        banner={banner}
        cardFilters={cardFilters}
        sorts={auctionSorts}
        searchBoxPlaceholder={formatMessage(
          searchLayoutMessages.defaultPlaceholder
        )}
        showSavedFilters
      >
        <BackendCardResultsFromGraphQL
          hitSlugs={hitSlugs}
          searchLoading={loading || filtersLoading}
          nbHits={searchCards?.nbHits ?? 0}
          sport={sport}
          onlyPrimary
          hideSorareUser
          showDesktopFilter
          removeFinishedAuctions
        />
      </BackendMarketSearchLayout>
    </GQLSearchContext>
  );
};

export const supportsBackendAuctionsSearch = (sport: Sport) =>
  sport in backendPrimaryMarketFiltersBySport;

export const BackendAuctionsSearch = ({ sport, banner }: Props) => {
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  if (!supportsBackendAuctionsSearch(sport)) return null;

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{ sport }}
    >
      <SearchCardsContextProvider
        commonMarket={false}
        manageFavoriteFilter={false}
      >
        <BackendAuctionsSearchContent sport={sport} banner={banner} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
